import Permissions from '@app/src/auth/permissions'
import paths from '@app/src/wf-constants/paths'
import { lazy } from 'react'
import type { RouteProps } from 'react-router-dom'

const RecommendedJourneyScene = lazy(() => import('@app/src/pages/RecommendedJourney/RecommendedJourneyScene'))
const PlatformReleasesScene = lazy(() => import('@app/src/pages/PlatformReleases/PlatformReleasesScene'))
const SourcingRecommendationScene = lazy(
  () => import('@app/src/pages/SourcingRecommendation/SourcingRecommendationScene'),
)
const FindingsScene = lazy(() => import('@app/src/pages/Findings/FindingsScene'))
const Logout = lazy(() => import('@app/src/components/Logout'))
const ConfigurationsScene = lazy(() => import('@app/src/pages/Configurations/ConfigurationsScene'))
const SustainabilityLibraryScene = lazy(() => import('@app/src/pages/Configurations/SustainabilityLibraryScene'))
const DashboardsScene = lazy(() => import('@app/src/pages/Dashboards/DashboardsScene'))
const FinanceDashboard = lazy(() => import('@app/src/pages/Dashboards/GroupCompaniesDashboard'))
const InvestmentDashboard = lazy(() => import('@app/src/pages/Dashboards/InvestmentDashboard'))
const SourcingDashboard = lazy(() => import('@app/src/pages/Dashboards/SourcingDashboard'))
const TransparencyDashboard = lazy(() => import('@app/src/pages/Dashboards/TransparencyDashboard'))
const ExpectationPage = lazy(() => import('@app/src/pages/Expectation/ExpectationPage'))
const ExpectationsScene = lazy(() => import('@app/src/pages/Expectations/ExpectationsScene'))
const NotificationsScene = lazy(() => import('@app/src/pages/Notifications/NotificationsScene'))
const CreateProductScene = lazy(() => import('@app/src/pages/Product/CreateProductScene'))
const ProductScene = lazy(() => import('@app/src/pages/ProductMapping/ProductSceneDispatcher'))
const ReceivedMappingRequestScene = lazy(() => import('@app/src/pages/Product/ReceivedMappingRequestScene'))
const EditQuestion = lazy(() => import('@app/src/pages/QuestionEditor/EditQuestion'))
const Questionnaire = lazy(() => import('@app/src/pages/Questionnaire'))
const ContactsCollection = lazy(() => import('@app/src/pages/ResourceCollection/Collections/ContactsCollection'))
const DataHubScene = lazy(() => import('@app/src/pages/ResourceCollection/Collections/DataHubScene'))
const EvaluationsCollection = lazy(() => import('@app/src/pages/ResourceCollection/Collections/EvaluationsCollection'))
const ManageRequestsScene = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ManageRequests/ManageRequestsScene'),
)
const ProductMappingCompaniesSceneDispatcher = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ProductMappingCompaniesSceneDispatcher'),
)
const ProductMappingContactsSceneDispatcher = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ProductMappingContactsSceneDispatcher'),
)
const ProductsSceneDispatcher = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ProductsSceneDispatcher'),
)
const ProductDueDiligenceSceneDispatcher = lazy(
  () =>
    import('@app/src/pages/ResourceCollection/Collections/ProductDueDiligenceMrt/ProductDueDiligenceSceneDispatcher'),
)
const ProviderRiskCollection = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ProviderRiskCollection'),
)
const ProvidersScene = lazy(() => import('@app/src/pages/ResourceCollection/Collections/Providers/ProvidersScene'))
const ProvidersSceneDispatcher = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ProvidersSceneDispatcher'),
)
const SupplyChainRelationsSceneDispatcher = lazy(
  () =>
    import('@app/src/pages/ResourceCollection/Collections/SupplyChainRelationsMrt/SupplyChainRelationsSceneDispatcher'),
)
const BenchmarkScene = lazy(() => import('@app/src/pages/Configurations/ConfigurationsPages/Benchmark/BenchmarkScene'))
const ReceivedMappingRequestSceneDispatcher = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ReceivedMappingRequestSceneDispatcher'),
)
const ReceivedMappingResponseSceneDispatcher = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/ReceivedMappingResponseSceneDispatcher'),
)
const RiskScreeningScene = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/RiskScreening/RiskScreeningScene'),
)
const SentMappingRequestSceneDispatcher = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/SentMappingRequestSceneDispatcher'),
)
const SubscriptionsCollection = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/SubscriptionsCollection'),
)
const TransparencyProviderProgressCollection = lazy(
  () => import('@app/src/pages/ResourceCollection/Collections/TransparencyProviderProgressCollection'),
)
const ResourceExplorerScene = lazy(() => import('@app/src/pages/ResourceExplorer'))
const SolutionSelectorScene = lazy(() => import('@app/src/pages/SolutionSelector/SolutionSelectorScene'))
const SupplyChainDueDiligenceReportScene = lazy(
  () =>
    import('@app/src/pages/SustainabilityReport/SupplyChainDueDiligenceStatement/SupplyChainDueDiligenceReportScene'),
)
const TemplateBuilderWrapper = lazy(() => import('@app/src/pages/TemplateBuilder/TemplateBuilder'))
const TopicalRiskSceneDispatcher = lazy(() => import('@app/src/pages/TopicalRisk/TopicalRiskSceneDispatcher'))
const UserSettingsScene = lazy(() => import('@app/src/pages/UserSettingsScene'))
const AllAssessmentsScene = lazy(() => import('./pages/Assessments/AllAssessments/AllAssessmentsScene'))
const AssessmentScene = lazy(() => import('./pages/Assessments/Assessment/AssessmentScene'))
const TransparencyAssessmentPage = lazy(
  () => import('./pages/Assessments/AssessmentCompanyPage/TransparencyAssessmentPage'),
)
const InsightsReport = lazy(() => import('./pages/SustainabilityReport/MonthlyInsightsReport/InsightsReportScene'))
const YourDataPage = lazy(() => import('./pages/Transparency/SustainabilityProfile'))
const YourDataRedirect = lazy(() => import('./pages/Transparency/SustainabilityProfile/YourDataRedirect'))
const MySustainabilityProfilePage = lazy(() => import('./pages/Transparency/MySustainabilityProfile'))

export interface RouteWithPermission {
  permission?: Permissions | Permissions[] // will be allowed if the user has any of the permission in the array
  path: string
  name: string
  exact: boolean
  component: RouteProps['component']
}

export const Routes: RouteWithPermission[] = [
  {
    name: 'home',
    path: '/',
    exact: true,
    component: SolutionSelectorScene,
  },
  {
    name: 'configurations',
    path: paths.configurations,
    exact: false,
    component: ConfigurationsScene,
    permission: Permissions.ACCESS_SOLUTION_CONFIGURATION,
  },
  {
    name: 'sustainability-library',
    path: paths.sustainabilityLibrary,
    exact: false,
    component: SustainabilityLibraryScene,
    permission: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    name: 'resourceExplorerScene',
    path: paths.resourceExplorer,
    exact: true,
    component: ResourceExplorerScene,
  },
  {
    name: 'providersScene',
    path: paths.providers,
    exact: true,
    component: ProvidersSceneDispatcher,
    permission: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    name: 'providersExisting',
    path: paths.providersExisting,
    exact: true,
    component: ProvidersScene,
    permission: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    name: 'benchmarkScene',
    path: paths.benchmarks,
    exact: true,
    component: BenchmarkScene,
    permission: Permissions.WORLDFAVOR_EMPLOYEE_ADMIN,
  },
  {
    name: 'productsCollection',
    path: paths.productsCollection,
    exact: true,
    component: ProductsSceneDispatcher,
  },
  {
    // Declared before `createProduct`/`product` below: `product`'s path (`:id?`) matches any single
    // path segment, "due-diligence" included, so this static route must win the match first.
    name: 'productDueDiligence',
    path: paths.productDueDiligence,
    exact: true,
    component: ProductDueDiligenceSceneDispatcher,
    permission: Permissions.SOURCING_USER,
  },
  {
    name: 'valueChainCollection',
    path: paths.mappingNodeCollection,
    exact: true,
    component: SupplyChainRelationsSceneDispatcher,
    permission: [Permissions.SOURCING_USER, Permissions.TRANSPARENCY_USER],
  },
  {
    name: 'mappingRequestCollection',
    path: paths.mappingRequestCollection,
    exact: true,
    component: ReceivedMappingRequestSceneDispatcher,
    permission: Permissions.PRODUCT_MAPPING_RESPOND_ACCESS,
  },
  {
    name: 'sentMappingRequestCollection',
    path: paths.sentMappingRequestCollection,
    exact: true,
    component: SentMappingRequestSceneDispatcher,
    permission: Permissions.SOURCING_USER,
  },
  {
    name: 'mappingRequest',
    path: paths.mappingRequest,
    exact: true,
    component: ReceivedMappingRequestScene,
    permission: Permissions.PRODUCT_MAPPING_RESPOND_ACCESS,
  },
  {
    name: 'createProduct',
    path: paths.createProduct,
    exact: true,
    component: CreateProductScene,
    permission: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    name: 'product',
    path: paths.product,
    exact: true,
    component: ProductScene,
  },
  {
    name: 'productMappingCompanies',
    path: paths.productMappingCompanies,
    exact: true,
    component: ProductMappingCompaniesSceneDispatcher,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'productMappingContacts',
    path: paths.productMappingContacts,
    exact: true,
    component: ProductMappingContactsSceneDispatcher,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'logout',
    path: paths.logout,
    exact: true,
    component: Logout,
  },
  {
    name: 'userSettings',
    path: paths.userSettings,
    exact: false,
    component: UserSettingsScene,
  },
  {
    name: 'reporting',
    path: paths.reporting,
    exact: true,
    component: Questionnaire,
  },
  {
    name: 'productReporting',
    path: paths.productReporting,
    exact: true,
    component: Questionnaire,
  },
  {
    name: 'contactsCollection',
    path: paths.contacts,
    exact: true,
    component: ContactsCollection,
  },
  {
    name: 'dataHubScene',
    path: paths.dataHubCollection,
    exact: true,
    component: DataHubScene,
    permission: Permissions.DATA_HUB_ACCESS,
  },
  {
    name: 'receivedMappingResponseCollection',
    path: paths.receivedMappingResponseCollection,
    exact: true,
    component: ReceivedMappingResponseSceneDispatcher,
  },
  {
    name: 'subscriptionsCollection',
    path: paths.subscriptions,
    exact: true,
    component: SubscriptionsCollection,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'evaluations',
    path: paths.evaluations,
    exact: true,
    component: EvaluationsCollection,
    permission: Permissions.EVALUATION_ACCESS,
  },
  {
    name: 'dashboard',
    path: paths.dashboard,
    exact: true,
    component: DashboardsScene,
  },
  {
    name: 'sourcingDashboard',
    path: paths.sourcingDashboard,
    exact: true,
    component: SourcingDashboard,
    permission: Permissions.SOURCING_USER,
  },
  {
    name: 'investmentDashboard',
    path: paths.investmentDashboard,
    exact: true,
    component: InvestmentDashboard,
    permission: Permissions.INVESTMENT_USER,
  },
  {
    name: 'financeDashboard',
    path: paths.financeDashboard,
    exact: true,
    component: FinanceDashboard,
    permission: Permissions.FINANCE_USER,
  },
  {
    name: 'notifications',
    path: paths.notifications,
    exact: true,
    component: NotificationsScene,
  },
  {
    name: 'transparencyOverview',
    path: paths.transparency,
    exact: true,
    component: TransparencyDashboard,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'transparencyDashboard',
    path: paths.transparencyDashboard,
    exact: true,
    component: TransparencyDashboard,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'transparencyBaselineAssessment',
    path: paths.transparencyBaselineAssessment,
    exact: true,
    component: TransparencyAssessmentPage,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'transparencyStandardAssessment',
    path: paths.transparencyStandardAssessment,
    exact: true,
    component: TransparencyAssessmentPage,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'editTemplate',
    path: paths.editTemplate,
    exact: true,
    component: TemplateBuilderWrapper,
  },
  {
    name: 'editQuestion',
    path: paths.questionnaireBuilderCustomQuestion,
    exact: true,
    component: EditQuestion,
  },
  {
    name: 'manageRequestCollection',
    path: paths.manageRequest,
    exact: true,
    component: ManageRequestsScene,
  },
  {
    name: 'yourData',
    path: paths.yourData,
    exact: true,
    component: YourDataPage,
  },
  {
    name: 'legacySustainabilityProfile',
    path: paths.legacySustainabilityProfile,
    exact: true,
    component: YourDataRedirect,
  },
  {
    name: 'transparencySustainabilityProfile',
    path: paths.transparencySustainabilityProfile,
    exact: true,
    component: MySustainabilityProfilePage,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'transparencyProviderProgress',
    path: paths.transparencyProviderProgress,
    exact: true,
    component: TransparencyProviderProgressCollection,
    permission: Permissions.TRANSPARENCY_USER,
  },
  {
    name: 'providerRiskCollection',
    path: paths.providerRisk,
    exact: true,
    component: ProviderRiskCollection,
  },
  {
    name: 'riskScreeningCollection',
    path: paths.riskScreening,
    exact: true,
    component: RiskScreeningScene,
  },
  {
    name: 'assessments',
    path: paths.assessments,
    exact: true,
    component: AssessmentScene,
  },
  {
    name: 'allAssessments',
    path: paths.allAssessments,
    exact: true,
    component: AllAssessmentsScene,
  },
  {
    name: 'insightsReport',
    path: paths.insightsReport,
    exact: true,
    component: InsightsReport,
    permission: Permissions.SUSTAINABILITY_REPORT_ACCESS,
  },
  {
    name: 'supplyChainDueDiligenceReport',
    path: paths.supplyChainDueDiligenceStatement,
    exact: true,
    component: SupplyChainDueDiligenceReportScene,
    permission: Permissions.SUSTAINABILITY_REPORT_ACCESS,
  },
  {
    name: 'topicalRisk',
    path: paths.topicalRisk,
    exact: true,
    component: TopicalRiskSceneDispatcher,
  },
  {
    name: 'expectations',
    path: paths.sourcingExpectationsList,
    exact: true,
    component: ExpectationsScene,
    permission: [
      Permissions.SOURCING_USER,
      Permissions.FINANCE_USER,
      Permissions.INVESTMENT_USER,
      Permissions.TRANSPARENCY_USER,
    ],
  },
  {
    name: 'expectation',
    path: paths.sourcingExpectation,
    exact: true,
    component: ExpectationPage,
    permission: [
      Permissions.SOURCING_USER,
      Permissions.FINANCE_USER,
      Permissions.INVESTMENT_USER,
      Permissions.TRANSPARENCY_USER,
    ],
  },
  {
    name: 'expectationProvider',
    path: paths.sourcingExpectationProvider,
    exact: true,
    component: ExpectationPage,
    permission: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    name: 'findings',
    path: paths.findings,
    exact: true,
    component: FindingsScene,
    permission: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    name: 'recommendedJourney',
    path: paths.recommendedJourney,
    exact: true,
    component: RecommendedJourneyScene,
    permission: [Permissions.SOURCING_USER, Permissions.FINANCE_USER, Permissions.INVESTMENT_USER],
  },
  {
    name: 'platformReleases',
    path: paths.platformReleases,
    exact: true,
    component: PlatformReleasesScene,
  },
  {
    name: 'sourcingRecommendation',
    path: paths.sourcingRecommendation,
    exact: true,
    component: SourcingRecommendationScene,
  },
]

export const SimplifiedRoutes = Routes.map(({ path }) => ({ path }))
